import {createSlice, nanoid, PayloadAction} from '@reduxjs/toolkit';
import api from '../api';
import {
  e57FileFormat,
  encompassFileFormat,
  potreeFileFormat,
  lasFileFormat
} from '../file-extensions';
import { TagSize } from '../types/tags';
import { ServerType } from '../types/web-sources';
import { SceneCameraState } from './camera-slice';

export interface PlanarConfig {
  width: number;
  height: number;
  cx: number;
  cy: number;
  fx: number;
  fy: number;
}

export interface CameraData {
  id: string;
  x: number;
  y: number;
  z: number;
  roll: number;
  pitch: number;
  yaw: number;
  path: string;
  name: string;
  config?: PlanarConfig;
}

export interface OrthoData {
  imageBounds: [];
  imageDataSplit: [];
  imageData: string;
  imageSize: [number, number];
  imageSizeOriginal: [number, number];
  imagePath: string;
  scaleX: number;
  scaleY: number;
}

export interface TagItemData {
  id: string;
  name: string;
  comment: string;
  date: string;
  x: number;
  y: number;
  z?: number;
  sceneState: SceneCameraState;
  annotations: TagAnnotation[];
}

export interface TagAnnotation {
  id: string;
  definition: object;
}

export interface TagData {
  size: TagSize;
  texture: string;
  items: TagItemData[];
}

export interface LineworkLayerData {
  id: string;
  name: string;
  visible: boolean;
  color: string;
}

export interface WebMapData {
  url: string;
  type: ServerType;
  public: boolean;
  layers: WebMapLayerData[];
}

export interface WebMapLayerData {
  id: string;
  name: string;
  visible: boolean;
  identifier: string;
}

interface XMLAlignment {
  id: string;
  name: string;
  length: number;
  staStart: number;
  about: string;
  geometry: object[];
}

export interface XMLData {
  info: object;
  units: object;
  alignments: XMLAlignment[];
  enu: boolean;
}

type assetData = OrthoData | CameraData[] | TagData
  | LineworkLayerData[] | XMLData | WebMapData


export interface Asset {
  id: string;
  name: string;
  path: string;
  saved: boolean;
  cloud: boolean;
  date: string;
  visible: boolean;
  folderID: string;
  type: AssetType;
  data: assetData;
}

export interface WebSourceAsset extends Asset {
  type: AssetType.Webmap;
  data: WebMapData;
}

export interface AssetInfo {
  name: string;
  path: string;
  folderID: string;
  type: AssetType;
  data?: assetData;
}

export enum AssetType {
  LAS = 0,
  Encompass = 1,
  Panoramic = 2,
  Unknown = 3,
  OrthoMosaic = 4,
  Planar = 5,
  E57Points = 6,
  Tag = 7,
  IFC = 8,
  Potree = 9,
  SHP = 10,
  DXF = 11,
  LandXML = 12,
  Webmap = 13
}

export const pointCloudType = (ext: string): AssetType => {
  if (ext === lasFileFormat) {
    return AssetType.LAS;
  } else if (ext === encompassFileFormat) {
    return AssetType.Encompass;
  } else if (ext === e57FileFormat) {
    return AssetType.E57Points;
  } else if (ext === potreeFileFormat) {
    return AssetType.Potree;
  } else {
    return AssetType.Unknown;
  }
};

const getByID = (state, assetID: string) => {
  return state.find(asset => asset.id === assetID);
};

const getAssetByTagID = (state, tagID: string): Asset => {
  for (let asset of state) {
    if (asset.type !== AssetType.Tag) continue;

    const tags = (asset.data as TagData).items;
    const tagIDs = tags.map(x => x.id);
    if (tagIDs.includes(tagID)) return asset;
  }
};

const getAssetByWebMapLayerID = (state, layerID: string): Asset => {
  for (let asset of state) {
    if (asset.type !== AssetType.Webmap) continue;

    const layers = (asset.data as WebMapData).layers;
    const layerIDs = layers.map(x => x.id);
    if (layerIDs.includes(layerID)) return asset;
  }
};

const getAssetByModelLayerID = (state, layerID: string): Asset => {
  for (let asset of state) {
    const isModelWithLayers = (asset.type === AssetType.DXF)
      || (asset.type === AssetType.SHP);
    if (!isModelWithLayers) continue;

    const layers = asset.data as LineworkLayerData[];
    const layerIDs = layers.map(x => x.id);
    if (layerIDs.includes(layerID)) return asset;
  }
};

const getAssetPayload = (item: AssetInfo): Asset => {
  return {
    id: nanoid(),
    name: item.name,
    path: item.path,
    date: new Date().toISOString(),
    type: item.type,
    folderID: item.folderID,
    visible: true,
    saved: false,
    cloud: false,
    data: item.data
  } as Asset;
};

export const assetsSlice = createSlice({
  name: 'assets',
  initialState: [] as Asset[],
  reducers: {
    addAsset: (state, action: PayloadAction<AssetInfo>) => {
      const asset = getAssetPayload(action.payload);
      state.push(asset);
    },
    addAssets: (state, action: PayloadAction<AssetInfo[]>) => {
      action.payload.forEach(payload => {
        const asset = getAssetPayload(payload);
        state.push(asset);
      });
    },
    updateAsset: (state, action: PayloadAction<{
      assetID: string,
      folderID?: string,
      visible?: boolean,
      name?: string,
      size?: TagSize,
      texture?: string,
      enu?: boolean
    }>) => {
      const {assetID, ...updates} = action.payload;

      const asset = getByID(state, assetID);
      if (!asset) return;

      for (let key in updates) {
        // Generic asset values
        if (asset.hasOwnProperty(key)) {
          asset[key] = updates[key];
        };

        // Data specific values
        if (asset.data && asset.data.hasOwnProperty(key)) {
          asset.data[key] = updates[key];
        };
      };
    },
    deleteAsset: (state, action: PayloadAction<string>) => {
      return state.filter(asset => asset.id !== action.payload);
    },

    toggleFolderVisibility: (state, action) => {
      const {folderID, visible} = action.payload;
      const assets = state.filter(asset => asset.folderID === folderID);

      assets.forEach(asset => {
        asset.visible = visible;
      });
    },
    setVisibleAssets: (state, action) => {
      const visibleAssets = action.payload;
      state.forEach(asset => {
        asset.visible = visibleAssets.includes(asset.id);
      });
    },
    setVisibleFolder: (state, action) => {
      const folderID = action.payload;
      state.forEach(asset => {
        asset.visible = asset.folderID === folderID;
      });
    },
    setSavedState: (state, action) => {
      state.forEach(asset => {
        asset.saved = true;
      });
    },

    /** Model files */
    updateModelLayer: (state, action: PayloadAction<{
      layerID: string,
      visible?: boolean,
      color?: string
    }>) => {
      const {layerID, color, visible} = action.payload;
      const asset = getAssetByModelLayerID(state, layerID);
      if (!asset) return;

      const layers = asset.data as LineworkLayerData[];
      const layer = layers.find(x => x.id === layerID);
      if (!layer) return;

      if ("visible" in action.payload) {
        layer.visible = visible;
      }

      if ("color" in action.payload) {
        layer.color = color;
      }
    },

    /** Web sources */
    updateWebMapLayer: (state, action: PayloadAction<{
      layerID: string,
      visible: boolean
    }>) => {
      const {layerID, visible} = action.payload;
      const asset = getAssetByWebMapLayerID(state, layerID);
      if (!asset) return;

      const layers = (asset.data as WebMapData).layers;
      const layer = layers.find(x => x.id === layerID);
      if (!layer) return;

      layer.visible = visible;
    },

    /** Tag Items */
    createTags: (state, action: PayloadAction<{
      assetID: string;
      tags: TagItemData[];
    }>) => {
      const {assetID, tags} = action.payload;

      const asset = getByID(state, assetID);
      if (!asset) return;

      createTagsHelper(asset, tags);

      // Update cloud site
      api.tags.create(assetID, tags);
    },
    deleteTag: (state, action: PayloadAction<string>) => {
      const tagID = action.payload;
      const asset = getAssetByTagID(state, tagID);
      if (!asset) return;

      deleteTagHelper(asset, tagID);

      // Update cloud site
      api.tags.delete(tagID);
    },
    updateTag: (state, action: PayloadAction<{
      tagID: string,
      name?: string,
      comment?: string,
      assetID?: string,
      annotations?: TagAnnotation[],
      sceneState?: SceneCameraState
    }>) => {
      const {tagID, ...updates} = action.payload;
      const asset = getAssetByTagID(state, tagID);
      if (!asset) return;

      const items = (asset.data as TagData).items;
      const tag = items.find(tag => tag.id === tagID);
      if (!tag) return;

      // Tags pulled from online might be missing certain attributes
      if (!tag.hasOwnProperty("annotations")) {
        tag.annotations = [];
      }

      for (let key in updates) {
        if (tag.hasOwnProperty(key)) {
          tag[key] = updates[key];
        };
      };

      // Specific case for asset ID, since we store our tags in a strange way
      if ("assetID" in updates) {
        const newAssetID = updates.assetID;
        const newAsset = state.find(asset => asset.id === newAssetID);
        deleteTagHelper(asset, tagID);
        createTagsHelper(newAsset, [tag]);
      }

      // Update modified date
      tag.date = new Date().toISOString();

      // Update cloud site
      api.tags.update(tagID, {...updates, date: tag.date});
    },

    /** Websocket specific */
    wsCreateAsset: (state, action: PayloadAction<Asset>) => {
      const asset = action.payload;
      if (getByID(state, asset.id)) return;

      state.push(asset);
    },
    wsUpdateAsset: (state, action: PayloadAction<Asset>) => {
      const {id, ...updates} = action.payload;
      let asset = getByID(state, id);
      if (!asset) return;

      for (let key in updates) {
        if (asset.hasOwnProperty(key)) {
          asset[key] = updates[key];
        };
      };
    },
    wsDeleteAsset: (state, action: PayloadAction<string>) => {
      const assetID = action.payload;
      return state.filter(asset => asset.id !== assetID);
    },
  }
});

const deleteTagHelper = (asset: Asset, tagID: string) => {
  const items = (asset.data as TagData).items
    .filter(item => item.id !== tagID);

  asset.data = {
    ...asset.data,
    items
  } as TagData;
};

const createTagsHelper = (asset: Asset, tags: TagItemData[]) => {
  const items = [...(asset.data as TagData).items, ...tags];

  asset.data = {
    ...asset.data,
    items
  } as TagData;
};

/** Create new asset */
export const createAsset = (options: {
  name: string;
  folderID: string;
  path?: string;
  type: AssetType;
  texture: string;
  size: TagSize;
}) => {
  const {name, folderID, type, path=null} = options;

  return async (dispatch, getState) => {

    let assetData;

    if (type === AssetType.Tag) {
      assetData = {
        size: options.size,
        texture: options.texture,
        items: []
      } as TagData;
    }

    dispatch(assetsSlice.actions.addAsset({
      folderID,
      name,
      path,
      type,
      data: assetData
    }));

    const assets = getState().assets as Asset[];
    const [asset] = assets.slice(-1);

    if (type === AssetType.Tag) {
      await api.tags.createCollection(folderID, asset);
    }

    return asset;
  };
};

/** Delete asset */
export const deleteAsset = (assetID: string) => {
  return (dispatch, getState) => {
    const assets = getState().assets as Asset[];
    const asset = assets.find(asset => asset.id === assetID);
    if (!asset) return;

    dispatch(assetsSlice.actions.deleteAsset(assetID));

    if (asset.type === AssetType.Tag) {
      api.tags.deleteCollection(assetID);
    }
  };
};

/** Update asset values */
export const updateAsset = (options: {
  assetID: string,
  name?: string,
  visible?: boolean,
  folderID?: string,
  texture?: string,
  size?: TagSize,
  enu?: boolean
}) => {
  const {assetID, ...updates} = options;

  return (dispatch, getState) => {
    const assets = getState().assets as Asset[];
    const asset = assets.find(asset => asset.id === assetID);
    if (!asset) return;

    dispatch(assetsSlice.actions.updateAsset(options));

    if (asset.type === AssetType.Tag) {
      api.tags.updateCollection(assetID, updates);
    }
  };
};

export default assetsSlice.reducer;

export const {
  wsCreateAsset,
  wsUpdateAsset,
  wsDeleteAsset,
  addAsset,
  addAssets,
  createTags,
  setVisibleAssets,
  updateWebMapLayer,
  updateModelLayer,
  toggleFolderVisibility,
  updateTag,
  setVisibleFolder,
  setSavedState,
  deleteTag
} = assetsSlice.actions;

export const selectAllAssets = state => {
  return state.assets as Asset[];
};

export const getVisibleAssets = (assets: Asset[]) => {
  return assets.filter(asset => asset.visible);
};

export const getModelAssets = (assets: Asset[]) => {
  return assets.filter(asset => {
    return (asset.type === AssetType.IFC)
      || (asset.type === AssetType.SHP)
      || (asset.type === AssetType.DXF);
  });
};

export const getTagAssets = (assets: Asset[]) => {
  return assets.filter(asset => {
    return asset.type === AssetType.Tag;
  });
};

export const getPointCloudAssets = (assets: Asset[]) => {
  return assets.filter(asset => {
    return (asset.type === AssetType.Encompass)
      || (asset.type === AssetType.LAS)
      || (asset.type === AssetType.E57Points)
      || (asset.type === AssetType.Potree);
  });
};

export const getCameraFileAssets = (assets: Asset[]) => {
  return assets.filter(asset => {
    return asset.type === AssetType.Panoramic
      || asset.type === AssetType.Planar;
  });
};

export const getOrthoAssets = (assets: Asset[]) => {
  return assets.filter(asset => {
    return asset.type === AssetType.OrthoMosaic;
  });
};

export const getLandXMLAssets = (assets: Asset[]) => {
  return assets.filter(asset => {
    return asset.type === AssetType.LandXML;
  });
};

export const getWebMapAssets = (assets: Asset[]) => {
  return assets.filter(asset => {
    return asset.type === AssetType.Webmap;
  });
};

